import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getCurrentPositions, getPositions, type CurrentPosition, type Position } from "../../api/client";
import { format } from "date-fns";

interface Props {
  portfolioId: number | null;
}

const fmtUSD = (v?: number | null) =>
  v == null
    ? "—"
    : new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(v);

const fmt = (v?: number | null, decimals = 4) =>
  v == null ? "—" : new Intl.NumberFormat("en-US", { minimumFractionDigits: 0, maximumFractionDigits: decimals }).format(v);

export default function PositionsTable({ portfolioId }: Props) {
  const navigate = useNavigate();
  const [positions, setPositions] = useState<CurrentPosition[]>([]);
  const [closedSymbols, setClosedSymbols] = useState<{ symbol: string; description?: string; asset_class?: string; realized_pnl?: number }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    getCurrentPositions(portfolioId)
      .then((res) => setPositions(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  // Derive closed positions: symbols in history with qty=0 not present in current positions
  useEffect(() => {
    if (!portfolioId) return;
    getPositions({ portfolio_id: portfolioId, limit: 5000 }).then((res) => {
      const allRecords = res.data as Position[];
      const currentSymbols = new Set(positions.map((p) => p.symbol));
      // Group by symbol: take the record with the latest report_date
      const latest: Record<string, Position> = {};
      for (const rec of allRecords) {
        if (rec.asset_class === "CASH") continue;
        if (!latest[rec.symbol] || rec.report_date > latest[rec.symbol].report_date) {
          latest[rec.symbol] = rec;
        }
      }
      const closed = Object.values(latest)
        .filter((rec) => !currentSymbols.has(rec.symbol) || Number(rec.quantity) === 0)
        .filter((rec) => !currentSymbols.has(rec.symbol))
        .sort((a, b) => a.symbol.localeCompare(b.symbol))
        .map((rec) => ({
          symbol: rec.symbol,
          description: rec.description,
          asset_class: rec.asset_class,
          realized_pnl: rec.realized_pnl,
        }));
      setClosedSymbols(closed);
    });
  }, [portfolioId, positions]);

  const totalValue = positions.reduce((sum, p) => sum + (p.market_value ?? 0), 0);
  const totalUnrealized = positions.reduce((sum, p) => sum + (p.unrealized_pnl ?? 0), 0);

  if (!portfolioId) return <p style={{ color: "#64748b" }}>Select a portfolio.</p>;
  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

  return (
    <div>
      <h2 style={{ margin: "0 0 8px", color: "#1e293b" }}>Current Positions</h2>

      {/* Summary cards */}
      <div style={{ display: "flex", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <SummaryCard label="Total Market Value" value={fmtUSD(totalValue)} color="#3b82f6" />
        <SummaryCard
          label="Total Unrealized P&L"
          value={fmtUSD(totalUnrealized)}
          color={totalUnrealized >= 0 ? "#10b981" : "#ef4444"}
        />
        <SummaryCard label="Positions Count" value={String(positions.length)} color="#8b5cf6" />
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              {["Symbol", "Description", "Asset Class", "Qty", "Cost Basis", "Market Price", "Market Value", "Unrealized P&L", "Realized P&L", "As Of"].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", whiteSpace: "nowrap", color: "#475569", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {positions.length === 0 && (
              <tr>
                <td colSpan={10} style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>
                  No positions found. Import a Flex Query XML to get started.
                </td>
              </tr>
            )}
            {positions.map((pos) => (
              <tr key={pos.symbol} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "8px 12px", fontWeight: 700 }}>
                  <button
                    onClick={() => navigate(`/position-history?symbol=${pos.symbol}`)}
                    title="View position history"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      color: "#3b82f6",
                      fontWeight: 700,
                      fontSize: 14,
                      textDecoration: "underline",
                      textDecorationStyle: "dotted",
                      fontFamily: "inherit",
                    }}
                  >
                    {pos.symbol}
                  </button>
                </td>
                <td style={{ padding: "8px 12px", color: "#64748b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{pos.description}</td>
                <td style={{ padding: "8px 12px" }}>
                  <span style={{ background: "#e2e8f0", borderRadius: 4, padding: "2px 6px", fontSize: 12 }}>{pos.asset_class ?? "STK"}</span>
                </td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmt(pos.quantity)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtUSD(pos.cost_basis)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtUSD(pos.market_price)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>{fmtUSD(pos.market_value)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right", color: (pos.unrealized_pnl ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                  {fmtUSD(pos.unrealized_pnl)}
                </td>
                <td style={{ padding: "8px 12px", textAlign: "right", color: (pos.realized_pnl ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                  {fmtUSD(pos.realized_pnl)}
                </td>
                <td style={{ padding: "8px 12px", fontSize: 12, color: "#94a3b8" }}>
                  {pos.latest_market_data_date ? format(new Date(pos.latest_market_data_date), "MMM d, yyyy") : format(new Date(pos.report_date), "MMM d, yyyy")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Closed Positions */}
      {closedSymbols.length > 0 && (
        <div style={{ marginTop: 32 }}>
          <h3 style={{ margin: "0 0 12px", color: "#64748b", fontSize: 15 }}>Closed Positions ({closedSymbols.length})</h3>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ background: "#f1f5f9" }}>
                  {["Symbol", "Description", "Asset Class", "Realized P&L"].map((h) => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", whiteSpace: "nowrap", color: "#475569", fontWeight: 600 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {closedSymbols.map((cs) => (
                  <tr key={cs.symbol} style={{ borderBottom: "1px solid #f1f5f9", opacity: 0.75 }}>
                    <td style={{ padding: "8px 12px", fontWeight: 700 }}>
                      <button
                        onClick={() => navigate(`/position-history?symbol=${cs.symbol}`)}
                        title="View position history"
                        style={{
                          background: "none",
                          border: "none",
                          padding: 0,
                          cursor: "pointer",
                          color: "#64748b",
                          fontWeight: 700,
                          fontSize: 14,
                          textDecoration: "underline",
                          textDecorationStyle: "dotted",
                          fontFamily: "inherit",
                        }}
                      >
                        {cs.symbol}
                      </button>
                    </td>
                    <td style={{ padding: "8px 12px", color: "#94a3b8", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>{cs.description}</td>
                    <td style={{ padding: "8px 12px" }}>
                      <span style={{ background: "#e2e8f0", borderRadius: 4, padding: "2px 6px", fontSize: 12 }}>{cs.asset_class ?? "STK"}</span>
                    </td>
                    <td style={{ padding: "8px 12px", textAlign: "right", color: (cs.realized_pnl ?? 0) >= 0 ? "#16a34a" : "#dc2626" }}>
                      {cs.realized_pnl != null ? fmtUSD(cs.realized_pnl) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: "12px 20px",
        minWidth: 180,
        borderTop: `3px solid ${color}`,
      }}
    >
      <div style={{ fontSize: 12, color: "#64748b", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}
